/**
 * Phase 2I — Panchanga location resolution (dashboard + pañchāṅga pages).
 * Priority: query → profile → live geolocation → browser TZ → Hyderabad fallback only.
 */

export const PANCHANGA_LOC_KEY = "vedapath_panchanga_loc";

/** Named fallback only when geolocation denied and no profile (not Muscat/India-specific). */
export const PANCHANGA_FALLBACK = {
  lat: 17.385,
  lon: 78.4867,
  tz_offset_minutes: 330,
  city: "Hyderabad",
  country: "IN",
  label: "Hyderabad",
};

export function browserTzOffsetMinutes() {
  return -new Date().getTimezoneOffset();
}

export function parseLocFromSearchParams(searchParams) {
  const lat = searchParams.get("lat");
  const lon = searchParams.get("lon");
  const tz = searchParams.get("tz_offset_minutes");
  if (lat == null || lon == null || tz == null) return null;
  const latN = Number(lat);
  const lonN = Number(lon);
  const tzN = Number(tz);
  if (!Number.isFinite(latN) || !Number.isFinite(lonN) || !Number.isFinite(tzN)) return null;
  return {
    lat: latN,
    lon: lonN,
    tz_offset_minutes: Math.round(tzN),
    source: "query",
  };
}

export function loadCachedLocation() {
  try {
    const raw = localStorage.getItem(PANCHANGA_LOC_KEY);
    if (!raw) return null;
    const s = JSON.parse(raw);
    if (typeof s.lat === "number" && typeof s.lon === "number" && typeof s.tz_offset_minutes === "number") {
      return s;
    }
  } catch (_) {}
  return null;
}

export function cacheLocation(loc) {
  try {
    localStorage.setItem(PANCHANGA_LOC_KEY, JSON.stringify(loc));
  } catch (_) {}
}

export function locQueryString(loc) {
  return `lat=${encodeURIComponent(loc.lat)}&lon=${encodeURIComponent(loc.lon)}&tz_offset_minutes=${encodeURIComponent(loc.tz_offset_minutes)}`;
}

export function tomorrowDateStr(tzOffsetMinutes) {
  const shifted = Date.now() + tzOffsetMinutes * 60000 + 86400000;
  const d = new Date(shifted);
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getUTCFullYear()}-${pad(d.getUTCMonth() + 1)}-${pad(d.getUTCDate())}`;
}

export function todayDateStr(tzOffsetMinutes) {
  const shifted = Date.now() + tzOffsetMinutes * 60000;
  const d = new Date(shifted);
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getUTCFullYear()}-${pad(d.getUTCMonth() + 1)}-${pad(d.getUTCDate())}`;
}

/** Reverse geocode for display label (Open-Meteo, no API key). */
export async function reverseGeocodeLabel(lat, lon) {
  try {
    const url = `https://geocoding-api.open-meteo.com/v1/reverse?latitude=${lat}&longitude=${lon}&language=en`;
    const res = await fetch(url);
    if (!res.ok) return null;
    const data = await res.json();
    const r = data?.results?.[0];
    if (!r) return null;
    return {
      city: r.name || r.admin1 || null,
      country: r.country_code || r.country || null,
    };
  } catch {
    return null;
  }
}

export function locationDisplayLabel(loc, meta) {
  const city = meta?.city || loc?.city || PANCHANGA_FALLBACK.city;
  const country = meta?.country || loc?.country || PANCHANGA_FALLBACK.country;
  const mode =
    meta?.source === "profile"
      ? "saved"
      : meta?.source === "query" || meta?.source === "auto"
        ? "auto"
        : meta?.source === "manual"
          ? "manual"
          : "default";
  return { city, country, mode };
}
